import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, GitCompareArrows, Plus, X, Loader2, Users2, PhoneCall, Send, Calendar, Award, Zap } from "lucide-react";
import api from "@/lib/api";
import { ScoreDial } from "@/components/performance/ScoreComponents";

const PERIODS = [
  { k: "today", t: "Hoje" }, { k: "week", t: "Semana" }, { k: "month", t: "Mês" },
  { k: "last_30d", t: "30 dias" }, { k: "last_90d", t: "90 dias" }, { k: "all", t: "Total" },
];

const METRICS = [
  { k: "leads_received", label: "Leads", type: "count", invert: false, icon: Users2 },
  { k: "sla_compliance", label: "SLA %", type: "sla", invert: false, icon: Zap },
  { k: "avg_response_minutes", label: "Tempo médio (min)", type: "sla_num", invert: true, icon: PhoneCall },
  { k: "estimates_sent", label: "Estimativas", type: "count", invert: false, icon: Send },
  { k: "estimate_to_rt", label: "Est → RT %", type: "conv", invert: false },
  { k: "rt_scheduled", label: "RT agendadas", type: "count", invert: false, icon: Calendar },
  { k: "deals_closed", label: "Negócios", type: "count", invert: false, icon: Award },
  { k: "lead_to_deal", label: "Lead → Negócio %", type: "conv", invert: false },
];

function fmtN(v) { return new Intl.NumberFormat("pt-PT").format(Number(v || 0)); }

function getMetric(entity, m) {
  if (m.type === "count") return entity.counts?.[m.k] ?? 0;
  if (m.type === "conv") return entity.conversions?.[m.k] ?? 0;
  if (m.type === "sla") return entity.sla?.compliance_pct ?? 0;
  if (m.type === "sla_num") return entity.sla?.avg_response_minutes ?? null;
  return 0;
}

function formatMetric(v, m) {
  if (v === null || v === undefined) return "—";
  if (m.type === "sla" || m.type === "conv") return `${(v).toFixed(1)}%`;
  if (m.type === "sla_num") return `${v} min`;
  return fmtN(v);
}

function findBest(entities, m) {
  const vals = entities.map(e => ({ id: e.id, v: getMetric(e, m) })).filter(x => x.v !== null && x.v !== undefined);
  if (vals.length === 0) return null;
  const target = m.invert ? Math.min(...vals.map(x => x.v)) : Math.max(...vals.map(x => x.v));
  // If everyone tied, no single winner
  if (vals.filter(x => x.v === target).length === vals.length) return null;
  const best = vals.find(x => x.v === target);
  return best?.id;
}

export default function PerformanceCompare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [kind, setKind] = useState(searchParams.get("kind") || "operator");
  const [period, setPeriod] = useState(searchParams.get("period") || "last_30d");
  const initialIds = (searchParams.get("ids") || "").split(",").filter(Boolean);
  const [selectedIds, setSelectedIds] = useState(initialIds);
  const [available, setAvailable] = useState([]);   // {id, name}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load candidates (operators or offices) from summary
  const loadAvailable = async () => {
    try {
      const r = await api.get(`/performance/summary?period=${period}`);
      if (kind === "operator") {
        setAvailable((r.data?.per_operator || []).map(o => ({ id: o.operator_id, name: o.operator_name })));
      } else {
        setAvailable((r.data?.per_office || []).map(o => ({ id: o.office, name: o.office })));
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadAvailable(); }, [kind, period]);

  const runCompare = async () => {
    if (selectedIds.length < 2) { setData(null); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind, ids: selectedIds.join(","), period });
      const r = await api.get(`/performance/compare?${params.toString()}`);
      setData(r.data);
      setSearchParams({ kind, ids: selectedIds.join(","), period });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a comparar");
    } finally { setLoading(false); }
  };

  useEffect(() => { runCompare(); }, [selectedIds.join(","), period, kind]);  // eslint-disable-line

  const toggleId = (id) => {
    setSelectedIds(list => list.includes(id) ? list.filter(x => x !== id) : [...list, id].slice(0, 6));
  };

  const entities = data?.entities || [];

  return (
    <div className="p-8 lg:p-12 max-w-[1400px] mx-auto space-y-6" data-testid="compare-root">
      <div>
        <Link to="/performance" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2" data-testid="back-perf">
          <ArrowLeft className="w-3 h-3" /> Performance do Atendimento
        </Link>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold flex items-center gap-1">
              <GitCompareArrows className="w-3 h-3" /> Comparação
            </div>
            <h1 className="font-display text-4xl lg:text-5xl tracking-tight">
              {kind === "operator" ? "Operadores" : "Escritórios"} lado a lado
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Selecciona 2 a 6 {kind === "operator" ? "operadores" : "escritórios"} para comparar performance.
              A célula vencedora em cada linha fica destacada — <em>volume não vence por si só</em>.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Kind toggle */}
            <div className="inline-flex rounded-lg border border-border overflow-hidden">
              <button onClick={() => { setKind("operator"); setSelectedIds([]); }}
                      className={`px-3 py-1.5 text-xs font-semibold ${kind === "operator" ? "bg-foreground text-background" : "bg-card text-foreground"}`}
                      data-testid="kind-operator">Operadores</button>
              <button onClick={() => { setKind("office"); setSelectedIds([]); }}
                      className={`px-3 py-1.5 text-xs font-semibold ${kind === "office" ? "bg-foreground text-background" : "bg-card text-foreground"}`}
                      data-testid="kind-office">Escritórios</button>
            </div>
            {/* Period */}
            <div className="inline-flex gap-1">
              {PERIODS.map(p => (
                <button key={p.k} onClick={() => setPeriod(p.k)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${period === p.k ? "bg-foreground text-background" : "bg-card border border-border text-foreground hover:bg-muted/40"}`}
                        data-testid={`cmp-period-${p.k}`}>{p.t}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Selector */}
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="compare-selector">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">
          Seleccionar {kind === "operator" ? "operadores" : "escritórios"} ({selectedIds.length}/6)
        </div>
        <div className="flex gap-2 flex-wrap">
          {available.length === 0 && <span className="text-xs text-muted-foreground">Nenhum candidato no período seleccionado.</span>}
          {available.map(a => {
            const sel = selectedIds.includes(a.id);
            return (
              <button key={a.id} onClick={() => toggleId(a.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${sel ? "bg-foreground text-background border-[#0F172A]" : "bg-card text-foreground border-border hover:bg-muted/40"}`}
                      data-testid={`candidate-${a.id}`}>
                {sel ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                {a.name}
              </button>
            );
          })}
        </div>
        {selectedIds.length < 2 && (
          <div className="mt-3 text-xs text-foreground">Selecciona pelo menos 2 para começar.</div>
        )}
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> A comparar…
        </div>
      )}

      {!loading && entities.length >= 2 && (
        <>
          {/* Score row (only for operators) */}
          {kind === "operator" && (
            <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${entities.length}, minmax(0, 1fr))` }} data-testid="compare-scores">
              {entities.map(e => {
                const s = e.score || {};
                return (
                  <div key={e.id} className="bg-card border border-border rounded-2xl p-5 text-center" data-testid={`compare-score-${e.id}`}>
                    <div className="flex justify-center mb-2">
                      <ScoreDial total={s.total || 0} emoji={s.emoji || "🔴"} size={100} strokeWidth={10} />
                    </div>
                    <div className="font-semibold text-sm text-foreground">{e.name}</div>
                    {s.blockers?.length > 0 && (
                      <div className="text-[10px] text-[hsl(var(--danger))] mt-1">{s.blockers.length} bloqueio(s)</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Metrics table */}
          <div className="bg-card border border-border rounded-2xl p-6 lg:p-8" data-testid="compare-metrics-table">
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Comparação métrica a métrica</div>
              <h3 className="font-display text-2xl">Onde cada um se destaca</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-2 w-48 text-[10px] uppercase tracking-[0.1em] font-bold text-muted-foreground">Métrica</th>
                    {entities.map(e => (
                      <th key={e.id} className="text-right py-2 px-3 text-xs font-semibold text-foreground">{e.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map(m => {
                    const best = findBest(entities, m);
                    return (
                      <tr key={m.k} className="border-b border-border/60" data-testid={`cmp-row-${m.k}`}>
                        <td className="py-3 px-2">
                          <div className="text-xs font-semibold text-foreground">{m.label}</div>
                          {m.invert && <div className="text-[10px] text-muted-foreground">Menor é melhor</div>}
                        </td>
                        {entities.map(e => {
                          const v = getMetric(e, m);
                          const isBest = best === e.id;
                          return (
                            <td key={e.id} className="text-right px-3 py-3">
                              <div className={`inline-flex items-center gap-1.5 tabular-nums ${isBest ? "font-bold text-[hsl(var(--success))]" : "text-foreground"}`}>
                                {isBest && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                                {formatMetric(v, m)}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && entities.length < 2 && selectedIds.length < 2 && (
        <div className="border-2 border-dashed border-border rounded-2xl p-12 text-center bg-card" data-testid="compare-empty">
          <GitCompareArrows className="w-10 h-10 mx-auto text-foreground mb-3" />
          <div className="font-display text-xl mb-1">Escolhe pelo menos 2 candidatos</div>
          <div className="text-sm text-muted-foreground">Selecciona no painel acima para comparar.</div>
        </div>
      )}
    </div>
  );
}
